import { memo, ReactElement } from "react";
import { TileGridRenderState } from "./RenderState/common";
import {
	convertDimensionToPx,
	GridCellPixelValues,
	GridPositionInPx
} from "./GridCellPixelValuesTracker";
import {
	Tile,
	TileCallbacks
} from "./Tile";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileContentDependencies } from "./useTileContentDependencies";
import { createObjectComparer } from "client/Misc/objectComparer";
import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import styles from "./TileGrid.module.less";
import { TilePlaceholder } from "./TilePlaceholder";

export type TileGridProps = {
	renderState: TileGridRenderState;
	isHidden: boolean;
	cellPixelValues: GridCellPixelValues;
	isTileGroupEditable: boolean;
	isInEditMode: boolean;
	userTimezone: TimeZone;
	tileContentDependencies: TileContentDependencies;
	displacementSinceTileDragStarted: GridPositionInPx | undefined;
	tileCallbacks: TileCallbacks;
};

const TileGridInternal = (props: TileGridProps): ReactElement => {
	const {
		renderState,
		isHidden,
		cellPixelValues,
		isTileGroupEditable,
		isInEditMode,
		userTimezone,
		tileContentDependencies,
		displacementSinceTileDragStarted,
		tileCallbacks
	} = props;

	const {
		placeholder,
		tiles
	} = renderState;

	return (
		<div
			{...setTrackingId("tile-grid")}
			className={classNames(
				styles.root,
				{
					[styles.root_editMode]: isInEditMode,
					[styles.root_editable]: isTileGroupEditable
				}
			)}
			// We use visibility instead of display so it still consumes the width it would
			// consume other wise
			style={{
				...convertDimensionToPx(renderState.dimension, cellPixelValues),
				visibility: isHidden ? "hidden" : undefined
			}}
		>
			{placeholder && (
				<TilePlaceholder
					placeholder={placeholder}
					cellPixelValues={cellPixelValues}
				/>
			)}

			{tiles.map((x): ReactElement => (
				<Tile
					key={x.data.id}
					renderState={x}
					cellPixelValues={cellPixelValues}
					isTileGroupEditable={isTileGroupEditable}
					isInEditMode={isInEditMode}
					userTimezone={userTimezone}
					tileContentDependencies={tileContentDependencies}
					tileGridDisplacementSinceTileDragStarted={
						x.isBeingDragged
							? displacementSinceTileDragStarted
							: undefined
					}
					tileCallbacks={tileCallbacks}
				/>
			))}
		</div>
	);
};
TileGridInternal.displayName = "TileGridInternal";

export const TileGrid = memo(
	TileGridInternal,
	createObjectComparer<TileGridProps>()
		.shallow("tileCallbacks")
		.equals([
			"cellPixelValues",
			"displacementSinceTileDragStarted",
			"isHidden",
			"isInEditMode",
			"isTileGroupEditable",
			"renderState",
			"tileContentDependencies",
			"userTimezone"
		])
		.getResult()
);
TileGrid.displayName = "TileGrid";
